import React from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Title,
  Divider,
  Grid,
  Card
} from '@mantine/core';
import {
  IconUsers,
  IconCalendar,
  IconClock,
  IconSchool,
  IconFlag,
  IconSettings
} from '@tabler/icons-react';

const BatchDetails = ({ batch }) => {
  const getStatusColor = () => {
    if (batch.bfc_admin_stop) return 'red';
    if (batch.bfc_max_batch_size_reached) return 'orange';
    if (batch.bfc_cap_time_reached) return 'yellow';
    return 'green';
  };

  const getStatusText = () => {
    if (batch.bfc_admin_stop) return 'Admin Stopped';
    if (batch.bfc_max_batch_size_reached) return 'Size Limit Reached';
    if (batch.bfc_cap_time_reached) return 'Cap Time Reached';
    return 'Active';
  };

  const StatCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="lg" fw={700}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  const FlagCard = ({ title, value, description }) => (
    <Paper withBorder p="sm">
      <Group justify="space-between">
        <Stack gap="xs">
          <Text size="sm" fw={500}>{title}</Text>
          <Text size="xs" c="dimmed">{description}</Text>
        </Stack>
        <Badge color={value ? 'red' : 'green'} variant="light">
          {value ? 'True' : 'False'}
        </Badge>
      </Group>
    </Paper>
  );

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Batch #{batch.batch_number}</Title>
        <Badge color={getStatusColor()} size="lg">
          {getStatusText()}
        </Badge>
      </Group>

      <Grid>
        <Grid.Col span={6}>
          <StatCard
            icon={IconUsers}
            title="Current Members"
            value={`${batch.members_count || 0} / ${batch.max_batch_size}`}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <StatCard
            icon={IconSchool}
            title="Curriculum"
            value={batch.curriculum?.title || 'Not Set'}
            color="purple"
          />
        </Grid.Col>
      </Grid>

      <Divider label="Schedule Information" labelPosition="center" />

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconCalendar size={20} color="var(--mantine-color-green-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Start Time</Text>
                <Text size="sm">
                  {new Date(batch.batch_start_time).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconClock size={20} color="var(--mantine-color-orange-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Cap Time</Text>
                <Text size="sm">
                  {new Date(batch.cap_time).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      <Divider label="Batch Flags" labelPosition="center" />

      <Stack>
        <FlagCard
          title="Max Batch Size Reached"
          value={batch.bfc_max_batch_size_reached}
          description="Automatically set when member count reaches max size"
        />
        <FlagCard
          title="Cap Time Reached"
          value={batch.bfc_cap_time_reached}
          description="Automatically set when current time exceeds cap time"
        />
        <FlagCard
          title="Admin Stop"
          value={batch.bfc_admin_stop}
          description="Manually controlled by admin to stop enrollment"
        />
      </Stack>

      <Paper withBorder p="md" mt="md">
        <Group>
          <IconSettings size={20} />
          <Stack gap="xs">
            <Text size="sm" fw={500}>Batch Information</Text>
            <Text size="xs" c="dimmed">
              Created: {new Date(batch.created_at).toLocaleString('en-IN')}
            </Text>
            <Text size="xs" c="dimmed">
              Last Updated: {new Date(batch.updated_at).toLocaleString('en-IN')}
            </Text>
          </Stack>
        </Group>
      </Paper>
    </Stack>
  );
};

export default BatchDetails;